import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { Dialog } from "@/components/ui/Dialog";
import { useDeleteApp } from "../hooks/useApps";

interface DeleteAppDialogProps {
  appName: string;
  open: boolean;
  onClose: () => void;
}

export function DeleteAppDialog({ appName, open, onClose }: DeleteAppDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const deleteApp = useDeleteApp();
  const [confirmation, setConfirmation] = useState("");

  const nameMatches = confirmation === appName;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (deleteApp.isPending) return;
    if (!nameMatches) return;

    deleteApp.mutate(appName, {
      onSuccess: () => {
        onClose();
        navigate({ to: "/apps" });
      },
    });
  };

  const handleClose = () => {
    if (!deleteApp.isPending) {
      setConfirmation("");
      deleteApp.reset();
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t("apps.deleteApp")}
      description={t("apps.deleteAppWarning")}
    >
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="delete-confirmation" className="text-sm font-medium text-subtle">
            {t("apps.deleteConfirm")}
          </label>
          <input
            id="delete-confirmation"
            type="text"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            placeholder={appName}
            data-autofocus
            readOnly={deleteApp.isPending}
            aria-disabled={deleteApp.isPending || undefined}
            className="
              h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-heading
              transition-colors duration-150
              placeholder:text-muted
              focus:outline-none focus:ring-2 focus:ring-danger/40 focus:border-danger
              aria-disabled:cursor-not-allowed aria-disabled:opacity-50
              hover:border-border-light
            "
          />
        </div>

        {deleteApp.error && (
          <p role="alert" className="mt-3 text-xs text-danger">
            {deleteApp.error.error ?? t("common.error")}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            aria-disabled={deleteApp.isPending || undefined}
            className="rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={!deleteApp.isPending && !nameMatches}
            aria-disabled={deleteApp.isPending || undefined}
            aria-busy={deleteApp.isPending || undefined}
            className="inline-flex items-center gap-2 rounded-lg bg-danger px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-danger/90 disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
          >
            {deleteApp.isPending ? t("apps.deleting") : t("apps.deleteApp")}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
